import { inject, Injectable } from '@angular/core';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { combineLatest, of, Subscription } from 'rxjs';
import { SeoService } from '@app/core/seo/seo.service';

// On every navigation, translates the leaf route's `titleKey`/`descriptionKey` (with optional `ogImage`/`ogType`) and hands them to SeoService; also clears the previous route's page-level JSON-LD. No `titleKey` means the app name alone.
@Injectable({ providedIn: 'root' })
export class AppTitleStrategy extends TitleStrategy {
  private readonly transloco = inject(TranslocoService);
  private readonly seo = inject(SeoService);

  private sub?: Subscription;

  override updateTitle(snapshot: RouterStateSnapshot): void {
    this.sub?.unsubscribe();
    this.seo.removeJsonLd('page');

    // Read the SEO keys and overrides from the deepest activated route's data.
    const data = this.leafData(snapshot);
    const titleKey = typeof data['titleKey'] === 'string' ? data['titleKey'] : undefined;
    const descriptionKey = typeof data['descriptionKey'] === 'string' ? data['descriptionKey'] : undefined;
    const type = typeof data['ogType'] === 'string' ? data['ogType'] : undefined;
    const image = typeof data['ogImage'] === 'string' ? data['ogImage'] : undefined;
    const path = snapshot.url;

    // If no title key is provided, show the app name alone (or any static route title).
    if (!titleKey) {
      this.seo.apply({ title: this.buildTitle(snapshot), path, image, type });
      return;
    }

    // Translate the title and optional description together; both re-emit on language change.
    this.sub = combineLatest([
      this.transloco.selectTranslate<string>(titleKey),
      descriptionKey ? this.transloco.selectTranslate<string>(descriptionKey) : of(''),
    ]).subscribe(([title, description]) => {
      this.seo.apply({ title, description, path, image, type });
    });
  }

  // Deepest activated route's data.
  private leafData(snapshot: RouterStateSnapshot): Record<string, unknown> {
    let route = snapshot.root;
    while (route.firstChild) route = route.firstChild;
    return route.data;
  }
}
